import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

type Props = {
  fileUrl: string;
  height?: string | number;
};

type Sheet = {
  name: string;
  cols: string[];
  rows: Array<Record<string, any>>;
};

export default function ExcelViewer({ fileUrl, height = 'calc(100vh - 140px)' }: Props) {
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [active, setActive] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(fileUrl, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const result: Sheet[] = wb.SheetNames.map((name) => {
          const ws = wb.Sheets[name];
          const arr: any[][] = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            blankrows: false,
            defval: '',
          });
          if (arr.length === 0) return { name, cols: [], rows: [] };
          // Première ligne = header. Si header vide, on génère Col A/B/C…
          const header = arr[0].map((c, i) =>
            String(c ?? '').trim() || String.fromCharCode(65 + i)
          );
          const rows = arr.slice(1).map((r) => {
            const o: Record<string, any> = {};
            header.forEach((h, i) => (o[h] = r[i]));
            return o;
          });
          return { name, cols: header, rows };
        });
        if (!cancelled) {
          setSheets(result);
          setActive(0);
        }
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  const current = sheets[active];
  const filteredSorted = useMemo(() => {
    if (!current) return [];
    const q = query.toLowerCase();
    let out = q
      ? current.rows.filter((r) =>
          current.cols.some((c) => String(r[c] ?? '').toLowerCase().includes(q))
        )
      : current.rows;
    if (sortCol) {
      out = [...out].sort((a, b) => {
        const va = a[sortCol];
        const vb = b[sortCol];
        const na = Number(va);
        const nb = Number(vb);
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return (na - nb) * sortDir;
        return String(va ?? '').localeCompare(String(vb ?? '')) * sortDir;
      });
    }
    return out;
  }, [current, query, sortCol, sortDir]);

  if (loading) {
    return (
      <div className="card card-meta" style={{ padding: 20 }}>
        Chargement du fichier Excel…
      </div>
    );
  }
  if (error) {
    return (
      <div className="card error-msg" style={{ margin: 0 }}>
        Impossible de lire ce fichier Excel : {error}
      </div>
    );
  }
  if (!current) {
    return (
      <div className="card card-meta" style={{ padding: 20 }}>
        Fichier vide.
      </div>
    );
  }

  return (
    <div
      className="excel-viewer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height,
        border: '1px solid var(--c-border)',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 10px',
          background: 'var(--c-nav)',
          borderBottom: '1px solid var(--c-border)',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        {sheets.map((s, i) => (
          <button
            key={s.name}
            onClick={() => {
              setActive(i);
              setSortCol(null);
              setQuery('');
            }}
            style={{
              padding: '4px 12px',
              fontSize: 12,
              background: i === active ? '#fff' : 'transparent',
              border: i === active ? '1px solid var(--c-border)' : '1px solid transparent',
              borderBottom: i === active ? '1px solid #fff' : '1px solid var(--c-border)',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontWeight: i === active ? 600 : 400,
              color: 'var(--c-text)',
              position: 'relative',
              top: 1,
            }}
          >
            {s.name}
            <span className="card-meta" style={{ fontSize: 10, marginLeft: 4 }}>
              ({s.rows.length})
            </span>
          </button>
        ))}
        <input
          type="text"
          placeholder="Filtrer dans cette feuille…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            marginLeft: 'auto',
            padding: '4px 10px',
            fontSize: 12,
            border: '1px solid var(--c-border)',
            borderRadius: 6,
            minWidth: 200,
          }}
        />
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
            tableLayout: 'auto',
          }}
        >
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th
                style={{
                  background: 'var(--c-nav)',
                  borderBottom: '1px solid var(--c-border)',
                  padding: '6px 8px',
                  fontWeight: 600,
                  textAlign: 'right',
                  color: 'var(--c-muted)',
                  width: 40,
                }}
              >
                #
              </th>
              {current.cols.map((c) => (
                <th
                  key={c}
                  onClick={() => {
                    if (sortCol === c) setSortDir((d) => (d === 1 ? -1 : 1));
                    else {
                      setSortCol(c);
                      setSortDir(1);
                    }
                  }}
                  style={{
                    background: 'var(--c-nav)',
                    borderBottom: '1px solid var(--c-border)',
                    padding: '6px 10px',
                    fontWeight: 600,
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  {c}
                  {sortCol === c && (
                    <span style={{ marginLeft: 4 }}>{sortDir === 1 ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FBFAF5' }}>
                <td
                  style={{
                    padding: '4px 8px',
                    color: 'var(--c-muted)',
                    fontSize: 11,
                    textAlign: 'right',
                    borderBottom: '1px solid #EFECE3',
                  }}
                >
                  {i + 1}
                </td>
                {current.cols.map((c) => (
                  <td
                    key={c}
                    style={{
                      padding: '4px 10px',
                      borderBottom: '1px solid #EFECE3',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {String(r[c] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
            {filteredSorted.length === 0 && (
              <tr>
                <td
                  colSpan={current.cols.length + 1}
                  className="card-meta"
                  style={{ textAlign: 'center', padding: 20 }}
                >
                  Aucune ligne ne correspond au filtre.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: '4px 10px',
          background: 'var(--c-nav)',
          borderTop: '1px solid var(--c-border)',
          fontSize: 11,
          color: 'var(--c-muted)',
        }}
      >
        {filteredSorted.length} / {current.rows.length} lignes · {current.cols.length} colonnes ·{' '}
        {sheets.length} feuille{sheets.length > 1 ? 's' : ''}
      </div>
    </div>
  );
}
